import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  CCard, CCardBody, CCardHeader, CTable, CTableBody, CTableHead,
  CTableRow, CTableHeaderCell, CTableDataCell
} from '@coreui/react';

import { apiClient } from 'services/Api';
import { jwtDecode } from 'jwt-decode';
import ToastNotification from "components/globals/ToastNotification";
import { triggerAlert } from 'components/globals/AlertNotification';

const CompetitionAnalysis = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [toast, setToast] = useState(null);
  const [analysisData, setAnalysisData] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, year: null, direction: 'asc' });

  const token = localStorage.getItem("access_token");
  const decodedToken = jwtDecode(token);

  const fetchCompetitionAnalysis = async () => {
    try {
      const response = await apiClient.get(`/competition-analysis/`);
      setAnalysisData(response.data);
    } catch (error) {
      triggerAlert('Erro ao carregar dados!', 'error');
      navigate("/empresas-concorrentes");
    }
  };

  useEffect(() => {
    fetchCompetitionAnalysis();
  }, [location.pathname]);

  const tableWrapperRef = useRef(null);
  useEffect(() => {
    const wrapper = tableWrapperRef.current;
    if (!wrapper) return;

    const updateHeaderHeight = () => {
      const theadRows = wrapper.querySelectorAll('thead tr');
      if (theadRows.length > 0) {
        const firstRowHeight = theadRows[0].offsetHeight;
        const totalHeight = Array.from(theadRows).reduce(
          (sum, row) => sum + row.offsetHeight,
          0
        );
        wrapper.style.setProperty('--first-header-height', `${firstRowHeight}px`);
        wrapper.style.setProperty('--header-height', `${totalHeight}px`);
      }
    };

    const raf = requestAnimationFrame(() => {
      updateHeaderHeight();
      const thead = wrapper.querySelector('thead');
      if (thead) {
        const resizeObserver = new ResizeObserver(updateHeaderHeight);
        resizeObserver.observe(thead);
        return () => resizeObserver.disconnect();
      }
    });

    return () => cancelAnimationFrame(raf);
  }, [analysisData]);

  const formatValue = (value, type) => {
    if (value === null || value === undefined || value === '') return '-';
    switch (type) {
      case 'currency':
        return new Intl.NumberFormat('pt-PT', {
          style: 'currency',
          currency: 'EUR',
          maximumFractionDigits: 0,
        }).format(Number(value));
      case 'percentage':
        return `${Number(value).toFixed(1)}%`;
      case 'integer':
        return new Intl.NumberFormat('pt-PT').format(Number(value));
      default:
        return value;
    }
  };

  const getSortedCompanies = (data, key) => {
    if (!key || !analysisData) return Object.entries(data).filter(([name]) => name !== 'STREAM');

    if (key === 'empresa') {
      return Object.entries(data)
        .filter(([name]) => name !== 'STREAM')
        .sort(([nameA], [nameB]) =>
          sortConfig.direction === 'asc'
            ? nameA.localeCompare(nameB)
            : nameB.localeCompare(nameA)
        );
    }

    const entries = Object.entries(data).filter(([name]) => name !== 'STREAM');

    return entries.sort(([nameA, dataA], [nameB, dataB]) => {
      if (sortConfig.year === 'avg') {
        const a = dataA.averages?.[key];
        const b = dataB.averages?.[key];
        const valA = a !== undefined && a !== null ? parseFloat(a) : -Infinity;
        const valB = b !== undefined && b !== null ? parseFloat(b) : -Infinity;
        return sortConfig.direction === 'asc' ? valA - valB : valB - valA;
      }

      const latestYear = sortConfig.year || Object.keys(dataA.indicators).sort().reverse()[0];
      const a = dataA.indicators[latestYear]?.[0]?.[key];
      const b = dataB.indicators[latestYear]?.[0]?.[key];
      const valA = a !== undefined && a !== null ? parseFloat(a) : -Infinity;
      const valB = b !== undefined && b !== null ? parseFloat(b) : -Infinity;
      return sortConfig.direction === 'asc' ? valA - valB : valB - valA;
    });
  };

  const renderUnifiedTable = (data) => {
    const allYears = Array.from(
      new Set(
        Object.values(data).flatMap(company => Object.keys(company.indicators))
      )
    ).sort();

    const indicatorMeta = {
      revenue: { label: 'VN', type: 'currency' },
      employees: { label: 'RH', type: 'integer' },
      ebitda: { label: 'EBITDA', type: 'currency' },
      net_income: { label: 'RL', type: 'currency' },
      personnel_costs: { label: 'Custos RH', type: 'currency' },
      export_rate: { label: 'Taxa Exp', type: 'percentage' },
      revenue_by_employees: { label: 'VN / RH', type: 'currency' },
      net_income_by_employees: { label: 'RL / RH', type: 'currency' },
      personnel_costs_by_employees: { label: 'C. Pessoal / RH', type: 'currency' },
    };

    const allKeys = Object.keys(indicatorMeta);

    const tableClasses = `analysis-table has-sticky-columns ${data.STREAM ? 'has-stream-row' : ''}`;

    return (
      <CTable striped bordered hover className={tableClasses}>
        <CTableHead>
          <CTableRow>
            <CTableHeaderCell
              className="sticky-column"
              style={{ minWidth: 300, cursor: 'pointer' }}
              rowSpan={2}
              onClick={() =>
                setSortConfig((prev) => ({
                  key: 'empresa',
                  year: null,
                  direction: prev.key === 'empresa' && prev.direction === 'asc' ? 'desc' : 'asc',
                }))
              }
            >
              Empresa
              {sortConfig.key === 'empresa' && (
                sortConfig.direction === 'asc' ? ' ▲' : ' ▼'
              )}
            </CTableHeaderCell>
            {allKeys.map((key, keyIndex) =>
              allYears.map((year) => (
                <CTableHeaderCell
                  key={`${key}-${year}`}
                  rowSpan={2}
                  style={{ minWidth: 75, cursor: 'pointer' }}
                  onClick={() =>
                    setSortConfig((prev) => {
                      const isSame = prev.key === key && prev.year === year;
                      return {
                        key,
                        year,
                        direction: isSame && prev.direction === 'asc' ? 'desc' : 'asc',
                      };
                    })
                  }
                >
                  {indicatorMeta[key].label}
                  <br />
                  {year}
                  {sortConfig.key === key && sortConfig.year === year && (
                    sortConfig.direction === 'asc' ? ' ▲' : ' ▼'
                  )}
                </CTableHeaderCell>
              ))
            )}
            <CTableHeaderCell colSpan={4} className="text-center">
              Média 3 anos
            </CTableHeaderCell>
          </CTableRow>
          <CTableRow>
            {[
              { key: 'revenue', label: 'VN' },
              { key: 'net_income', label: 'RL' },
              { key: 'net_income_by_employees', label: 'RL / RH' },
              { key: 'personnel_costs_by_employees', label: 'C. Pessoal / RH' },
            ].map(({ key, label }) => (
              <CTableHeaderCell
                key={`media-${key}`}
                style={{ minWidth: 75, cursor: 'pointer' }}
                onClick={() =>
                  setSortConfig((prev) => ({
                    key,
                    year: 'avg',
                    direction: prev.key === key && prev.year === 'avg' && prev.direction === 'asc' ? 'desc' : 'asc',
                  }))
                }
              >
                {label}
                {sortConfig.key === key && sortConfig.year === 'avg' && (
                  sortConfig.direction === 'asc' ? ' ▲' : ' ▼'
                )}
              </CTableHeaderCell>
            ))}
          </CTableRow>
        </CTableHead>

        <CTableBody>
          {data.STREAM && (
            <CTableRow className="stream-row">
              <CTableDataCell className="sticky-column" style={{ minWidth: 200 }}>
                STREAM
              </CTableDataCell>
              {allKeys.flatMap((key, keyIndex) =>
                allYears.map((year) => (
                  <CTableDataCell
                    key={`STREAM-${key}-${year}`}
                    className={`indicator-col indicator-${keyIndex % 2 === 0 ? 'a' : 'b'}`}
                  >
                    {formatValue(data.STREAM.indicators[year]?.[0]?.[key], indicatorMeta[key]?.type)}
                  </CTableDataCell>
                ))
              )}
              {['revenue', 'net_income', 'net_income_by_employees', 'personnel_costs_by_employees'].map((avgKey) => (
                <CTableDataCell key={`STREAM-avg-${avgKey}`}>
                  {formatValue(data.STREAM.averages?.[avgKey], indicatorMeta[avgKey]?.type)}
                </CTableDataCell>
              ))}
            </CTableRow>
          )}

          {getSortedCompanies(data, sortConfig.key).map(([companyName, companyData]) => {
            const averages = companyData.averages || {};
            return (
              <CTableRow key={companyName}>
                <CTableDataCell className="sticky-column" style={{ minWidth: 200 }}>
                  {companyName}
                </CTableDataCell>
                {allKeys.flatMap((key, keyIndex) =>
                  allYears.map((year) => (
                    <CTableDataCell
                      key={`${companyName}-${key}-${year}`}
                      className={`indicator-col indicator-${keyIndex % 2 === 0 ? 'a' : 'b'}`}
                    >
                      {formatValue(companyData.indicators[year]?.[0]?.[key], indicatorMeta[key]?.type)}
                    </CTableDataCell>
                  ))
                )}
                {['revenue', 'net_income', 'net_income_by_employees', 'personnel_costs_by_employees'].map((avgKey) => (
                  <CTableDataCell key={`${companyName}-avg-${avgKey}`}>
                    {formatValue(averages?.[avgKey], indicatorMeta[avgKey]?.type)}
                  </CTableDataCell>
                ))}
              </CTableRow>
            );
          })}
        </CTableBody>
      </CTable>
    );
  };

  if (!analysisData) return null;

  const combinedData = {
    STREAM: analysisData.STREAM,
    ...analysisData.companies
  };

  return (
    <div className="container-fluid mt-4">
      <ToastNotification toast={toast} setToast={setToast} />

      <CCard>
        <CCardHeader>
          <h4>Análise da Concorrência</h4>
        </CCardHeader>
        <CCardBody style={{ padding: 0 }}>
          <div
            ref={tableWrapperRef}
            className="analysis-table-wrapper"
            style={{
              maxHeight: '80vh',
              overflow: 'auto',
              overflowX: 'scroll',
              scrollbarGutter: 'stable',
            }}
          >
            {renderUnifiedTable(combinedData)}
          </div>
        </CCardBody>
      </CCard>
    </div>
  );
};

export default CompetitionAnalysis;
